/**
 * ================================================================
 * HOOK DE PERMISSÕES
 * Simplifica verificações de permissão e cargos
 * Data: 03/11/2025
 * ================================================================
 */

import { usePermissions } from '../contexts/PermissionContext';

export const usePermission = () => {
  const { permissoes, cargo, tipoEmpresa, carregando, podeAcessar } = usePermissions();
  
  return {
    // ========================================
    // VERIFICAÇÕES DE CARGO
    // ========================================
    
    isAdmin: cargo === 'admin',
    isPsicologo: cargo === 'psicologo',
    isEstudante: cargo === 'estudante',
    isAtendente: cargo === 'atendente',
    
    // ========================================
    // TIPOS DE EMPRESA
    // ========================================
    
    isClinica: tipoEmpresa === 'clinica',
    isPsicologoIndividual: tipoEmpresa === 'psicologo_individual',
    isContaEstudante: tipoEmpresa === 'estudante',
    
    // ========================================
    // VERIFICAR PERMISSÃO GENÉRICA
    // ========================================
    
    podeAcessar,
    
    // ========================================
    // PERMISSÕES ESPECÍFICAS - PACIENTES
    // ========================================
    
    podeCriarPacientes: podeAcessar('pacientes', 'criar'),
    podeVerTodosPacientes: podeAcessar('pacientes', 'ver_todos'),
    podeEditarTodosPacientes: podeAcessar('pacientes', 'editar_todos'),
    podeDeletarPacientes: podeAcessar('pacientes', 'deletar'),
    
    // ========================================
    // PERMISSÕES ESPECÍFICAS - CONSULTAS
    // ========================================
    
    podeCriarConsultas: podeAcessar('consultas', 'criar'),
    podeVerTodasConsultas: podeAcessar('consultas', 'ver_todas'),
    podeEditarTodasConsultas: podeAcessar('consultas', 'editar_todas'),
    podeCancelarConsultas: podeAcessar('consultas', 'cancelar'),
    
    // ========================================
    // PERMISSÕES ESPECÍFICAS - PRONTUÁRIOS
    // ========================================
    
    podeCriarProntuarios: podeAcessar('prontuarios', 'criar'),
    podeVerTodosProntuarios: podeAcessar('prontuarios', 'ver_todos'),
    podeEditarTodosProntuarios: podeAcessar('prontuarios', 'editar_todos'),
    podeDeletarProntuarios: podeAcessar('prontuarios', 'deletar'),
    
    // ========================================
    // PERMISSÕES ESPECÍFICAS - FINANCEIRO
    // ========================================
    
    podeVerFinanceiro: podeAcessar('financeiro', 'ver'),
    podeRegistrarPagamentos: podeAcessar('financeiro', 'registrar'),
    podeVerRelatoriosFinanceiros: podeAcessar('financeiro', 'relatorios'),
    
    // ========================================
    // PERMISSÕES ESPECÍFICAS - USUÁRIOS
    // ========================================
    
    podeCriarUsuarios: podeAcessar('usuarios', 'criar'),
    podeEditarUsuarios: podeAcessar('usuarios', 'editar'),
    podeDeletarUsuarios: podeAcessar('usuarios', 'deletar'),
    podeVerTodosUsuarios: podeAcessar('usuarios', 'ver_todos'),
    
    // ========================================
    // PERMISSÕES ESPECÍFICAS - CONFIGURAÇÕES
    // ========================================
    
    podeEditarConfiguracoes: podeAcessar('configuracoes', 'empresa'),
    podeEditarSistema: podeAcessar('configuracoes', 'sistema'),
    
    // ========================================
    // PERMISSÕES ESPECÍFICAS - RELATÓRIOS
    // ========================================
    
    podeVerTodosRelatorios: podeAcessar('relatorios', 'ver_todos'),
    podeVerRelatoriosConsolidados: podeAcessar('relatorios', 'consolidados'),
    
    // ========================================
    // VERIFICAÇÕES COMPOSTAS
    // ========================================
    
    // Pode gerenciar usuários (criar, editar, deletar)
    podeGerenciarUsuarios: cargo === 'admin' && tipoEmpresa !== 'estudante',
    
    // Pode ver dados financeiros
    temAcessoFinanceiro: ['admin', 'psicologo', 'estudante'].includes(cargo),
    
    // Pode ver prontuários
    temAcessoProntuarios: ['admin', 'psicologo', 'estudante'].includes(cargo),
    
    // Pode ver todos os dados (não só os próprios)
    veTodasInformacoes: ['admin', 'estudante', 'atendente'].includes(cargo),
    
    // Só vê dados próprios
    veSomentePropriosDados: cargo === 'psicologo',
    
    // Tem limites de uso
    temLimites: tipoEmpresa === 'estudante',
    
    // ========================================
    // DADOS BRUTOS
    // ========================================
    
    cargo,
    tipoEmpresa,
    permissoes,
    carregando
  };
};
